import type { SpinButtonPort } from "./SpinButtonPort";

/** The intents the spin input surface produces; the presenter decides what each means. */
export interface SpinInputListener {
  pointerDown(): void;
  /**
   * A press ended; `onButton` only for a release over the spin face. The policy may already have
   * ended that press (a block, `stop()`).
   */
  pointerUp(onButton: boolean): void;
  keyDown(): void;
  keyUp(): void;
  slamClick(): void;
}

/** The window listeners the sources need (the global by default; a fake in tests). */
export interface SpinInputWindow {
  addEventListener(type: string, listener: (e: Event) => void, capture?: boolean): void;
  removeEventListener(type: string, listener: (e: Event) => void, capture?: boolean): void;
}

/**
 * Where spin input comes from: the spin button's presses as its element reports them (the element
 * hears the release itself, wherever it lands, and says whether it landed on the spin face) and the
 * spacebar (repeat filtered). The only file in the chain that knows a key exists.
 */
export class SpinInputSources {
  private readonly keyDownHandler = (e: Event): void => {
    const key = e as KeyboardEvent;
    if (key.code !== "Space" || key.repeat) return;
    this.listener.keyDown();
  };
  private readonly keyUpHandler = (e: Event): void => {
    if ((e as KeyboardEvent).code !== "Space") return;
    this.listener.keyUp();
  };
  private readonly keyReleaseHandler = (): void => this.listener.keyUp();
  /**
   * A `keyup` that never arrives because the window lost focus (Cmd/Alt-Tab, a click into another
   * app) or the tab was hidden. Without it the hold policy keeps the press latched and chains
   * real-money spins with nothing held down.
   *
   * Bound in the BUBBLE phase: `blur` does not bubble, so a capture-phase listener would also see
   * focus moving between bar buttons and release the key on every Tab press. A release with no press
   * outstanding is a no-op in `pressEnd`, so firing this on the visible half of `visibilitychange`
   * costs nothing.
   */
  private static readonly KEY_RELEASE_EVENTS = ["blur", "visibilitychange"] as const;

  public constructor(
    private readonly panel: SpinButtonPort,
    private readonly listener: SpinInputListener,
    private readonly window: SpinInputWindow | null = typeof globalThis.window !== "undefined" ? globalThis.window : null,
  ) {
    this.panel.bind({
      onSlamClick: () => this.listener.slamClick(),
      onPressStart: () => this.listener.pointerDown(),
      onPressEnd: (onButton) => this.listener.pointerUp(onButton),
    });
  }

  public start(): void {
    this.window?.addEventListener("keydown", this.keyDownHandler);
    this.window?.addEventListener("keyup", this.keyUpHandler);
    for (const type of SpinInputSources.KEY_RELEASE_EVENTS) this.window?.addEventListener(type, this.keyReleaseHandler);
  }

  public stop(): void {
    this.window?.removeEventListener("keydown", this.keyDownHandler);
    this.window?.removeEventListener("keyup", this.keyUpHandler);
    for (const type of SpinInputSources.KEY_RELEASE_EVENTS) this.window?.removeEventListener(type, this.keyReleaseHandler);
  }
}
